import React, { useEffect, useMemo } from "react";
import store from "store";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useHistory } from "react-router-dom";
import moment from "moment";
// mui
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import Container from "@material-ui/core/Container";
import { ThemeProvider } from "@material-ui/core/styles";
// theme
import theme from "../../assets/v3/theme";
// apis
import {
  getRegistrationFormConfig,
  updateStudentDetails,
} from "../../apis/UserService";
// hooks
import useFetch from "../../hooks/useFetch";
import { useSnackbar } from "../../context/snackbar";
// fields
import { PreviousSchoolDetails } from "./utils";
import { PageTitle, useStyles } from ".";
// utils
import { renderFormField } from "./RenderFormField";
import { hostnamesMap } from "../../components/LogoURLMap";
import TextScroll from "./TextScroll";

const PreviousSchoolDetailsForm = () => {
  const classes = useStyles();
  const history = useHistory();
  const instituteId = hostnamesMap[window.location.hostname];
  const sid = store.get("user")._id;
  const page = "previous-school-details";
  const [l1, e1, d1, f1, r1] = useFetch(getRegistrationFormConfig);
  const [l2, e2, d2, f2, r2] = useFetch(updateStudentDetails);
  const payload = d1 ? d1.payload : null;
  const scrollText =
    payload && payload.scroll_text ? payload.scroll_text : null;
  const fields = payload && payload.fields ? payload.fields : [];
  const details = d1 ? d1.details : {};
  const next = payload ? payload.next : null;
  const previous = payload ? payload.previous : null;
  const { openSnackbar } = useSnackbar();

  const formatRouteName = (route) => {
    if (!route) return "";
    return route
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const fieldMap = useMemo(() => {
    return PreviousSchoolDetails.reduce((map, field) => {
      map[field.field] = field;
      return map;
    }, {});
  }, []);

  const initialValues = useMemo(() => {
    return fields.reduce((values, item) => {
      const myKey =
        fieldMap && fieldMap[item.field] ? fieldMap[item.field].key : "";
      if (myKey && details && details.preSchoolDetails) {
        values[item.field] = myKey.includes(".")
          ? details[myKey.split(".")[0]][myKey.split(".")[1]]
          : details[myKey];

        if (myKey === "dateOfBirth" && values[item.field]) {
          values[item.field] = moment(values[item.field]).format("YYYY-MM-DD");
        }
      } else {
        values[item.field] = "";
      }
      return values;
    }, {});
  }, [fields, details]);

  useEffect(() => {
    f1({
      instituteId,
      page,
      sid,
    });
    window.scrollTo(0, 0);

    return () => {
      r1();
    };
  }, []);

  useEffect(() => {
    if (d1) {
      console.log("Registration Form Config:", d1);
    }
    if (e1) {
      console.error("Failed to fetch registration form config:", e1);
      openSnackbar("Failed to fetch registration form config", "error");
    }
  }, [d1, e1]);

  useEffect(() => {
    if (d2) {
      console.log("Form Submitted", d2);
      if (d2.data.status === "SUCCESS") {
        openSnackbar("Previous school details saved successfully", "success");
      } else {
        openSnackbar("Failed to save previous school details", "error");
      }
      if (next) {
        history.push(`/${next}`);
      }
    }
    if (e2) {
      console.error("Failed to submit form", e2);
    }

    return () => {
      r2();
    };
  }, [d2, e2]);

  const validationSchema = Yup.object().shape(
    fields.reduce((schema, item) => {
      const field = fieldMap[item.field];
      console.log("field", field);
      if(!field) return schema;
      const cond = item.con;
      let validator = Yup.string();
      if (field.type === "email") {
        validator = validator.email("Invalid email format");
      }
      if (field.type === "text" && item.field !== "address") {
        validator = validator.matches(
          /^[a-zA-Z\s]*$/,
          "Only alphabets and spaces are allowed"
        );
      }
      if (field.type === "select") {
        validator = validator.oneOf(
          field.options,
          `Must be one of: ${field.options.join(", ")}`
        );
      }
      if (cond === "R") {
        validator = validator.required("Required");
      }
      schema[field.field] = validator;
      return schema;
    }, {})
  );

  return (
    <ThemeProvider theme={theme}>
      {scrollText && (
        <TextScroll
          text={scrollText}
          textStyles={{
            fontWeight: "bolder",
            fontSize: "1.5rem",
            background: `-webkit-linear-gradient(#ED1B24, #074EA0)`,
            "-webkit-background-clip": "text",
            "-webkit-text-fill-color": "transparent",
          }}
        />
      )}
      <Box p={1} bgcolor={"background.default"} minHeight="100vh" width="100%">
        <Container maxWidth="md" className={classes.formContainer}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={(values) => {
                  const data = Object.keys(values).reduce((obj, f) => {
                    const myKey =
                      fieldMap && fieldMap[f] ? fieldMap[f].key : "";
                    if (myKey) {
                      obj[myKey] = values[f];
                    } else {
                      obj[f] = values[f];
                    }
                    return obj;
                  }, {});
                  f2({ sid, data, page });
                }}
                enableReinitialize={true}
              >
                {({ values, setFieldValue, touched, errors }) => (
                  <Form>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="h6">
                          <b>Previous School Details</b>
                        </Typography>
                      </Grid>

                      <Grid item xs={6}>
                        <Box
                          display="flex"
                          justifyContent="flex-end"
                          width={"100%"}
                          height="100%"
                          alignItems="center"
                        >
                          <Typography variant="caption">
                            next: <b>{formatRouteName(next)}</b>
                          </Typography>
                        </Box>
                      </Grid>
                      {fields.map((field) => {
                        if (!field) return null;
                        if (!field.field) return null;
                        if (!fieldMap[field.field]) return null;
                        const fieldDescription = fieldMap[field.field];
                        return (
                          <Grid item xs={12} sm={6} md={4} key={field.field}>
                            {renderFormField({
                              field: fieldDescription,
                              handleChange: setFieldValue,
                              value: values[field.field],
                              error:
                                touched[field.field] && errors[field.field],
                              helperText: errors[field.field],
                              optionClassName: classes.option,
                              labelClassName: classes.label,
                              required: field ? field.con === "R" : false,
                              readOnly: l1,
                            })}
                          </Grid>
                        );
                      })}
                      <Grid item xs={12}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="flex-end"
                          flexGrow={1}
                          width="100%"
                        >
                          <Button
                            variant="outlined"
                            color="secondary"
                            onClick={() => {
                              if (previous) {
                                history.push(`/${previous}`);
                              }
                            }}
                            disabled={l1 || l2}
                            className={classes.blueGradientBtn2}
                          >
                            Previous
                          </Button>
                          <Button
                            type="submit"
                            variant="contained"
                            color="large"
                            disabled={l1 || l2}
                            className={classes.blueGradientBtn}
                          >
                            {l2 ? "Saving..." : "Next"}
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Form>
                )}
              </Formik>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default PreviousSchoolDetailsForm;
